import React, { useState } from 'react'
import {
  AppButton,
  AppDialog,
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  AppDialogHeader,
  AppDialogTitle,
} from '@easyllama/ui-components'
import PlansDialog from '~/organisms/Billing/PlansDialog'
import ContactSalesDialog from '~/molecules/Billing/ContactSalesDialog'
import { AppContext, useApp } from '~/context/appContext'
import { sanitizeHtml } from '~/utils/commonUtils'
import { ANNOUNCEMENT_EVENTS } from '~/service/Announcements/fetchApiClient'
import { useAnnouncementEvents } from './useAnnouncementEvents'
import { useAnnouncementBilling } from './useAnnouncementBilling'
import {
  ANNOUNCEMENT_BODY_ALLOWED_ATTR,
  ANNOUNCEMENT_BODY_ALLOWED_TAGS,
  ANNOUNCEMENT_BODY_SANITIZE_OPTIONS,
  UPGRADE_NOW_TYPE,
  ctaTarget,
  isModifiedClick,
} from './announcementBody'

// Event contract, carried over from the Stimulus controller this replaces:
//
//   mount           -> viewed        the only event that moves a modal's suppression gate
//   X               -> closed        server stamps permanently_dismissed_at
//   Escape          -> nothing       hides for this page; returns on the next
//   backdrop click  -> nothing       inert, so a stray click cannot retire the announcement
//   "Maybe later"   -> secondary_cta server snoozes; never navigates
//   primary         -> primary_cta   then navigates, or opens the plans dialog in place
//   plans dialog    -> billing_viewed the dialog reached the reader, not just the click
//   contact link    -> nothing       see handleContactClick
const InAppAnnouncementModal = ({
  headline,
  bodyHtml,
  contactCta,
  mediaUrl,
  primaryCta,
  secondaryCta,
  eventsUrl,
  renderToken,
  preview = false,
}) => {
  const appContext = useApp()
  const { company } = appContext
  const [open, setOpen] = useState(true)
  const [plansDialogOpen, setPlansDialogOpen] = useState(false)
  const [contactSalesOpen, setContactSalesOpen] = useState(false)

  const isUpgradeNowCta = primaryCta?.type === UPGRADE_NOW_TYPE
  // Null for anything we will not follow, which also drops the button - see ctaTarget.
  const primaryTarget = ctaTarget(primaryCta?.url)
  const report = useAnnouncementEvents({ eventsUrl, renderToken, preview, viewOnMount: true })
  const billingContextValue = useAnnouncementBilling(appContext, { enabled: isUpgradeNowCta, renderToken })

  // Every exit that is not a rejection: `closed` would file it as one.
  const closeSilently = () => setOpen(false)

  // Only the X reaches this: Escape and click-outside are intercepted below, so `closed` -
  // a permanent dismissal server-side - is never spent on a reflex or a stray click.
  const handleOpenChange = (nextOpen) => {
    if (nextOpen) return

    report(ANNOUNCEMENT_EVENTS.CLOSED)
    setOpen(false)
  }

  // Inert, as the Stimulus controller's overlay was.
  const handleInteractOutside = (event) => event.preventDefault()

  // Closes, but silently: the keypress may be a reflex. Returns until views_count is spent.
  const handleEscapeKeyDown = (event) => {
    event.preventDefault()
    closeSilently()
  }

  // "Maybe later": the helper sends url: null for this placement, and the server snoozes.
  const handleSecondaryClick = () => {
    report(ANNOUNCEMENT_EVENTS.SECONDARY_CTA)
    closeSilently()
  }

  const handlePrimaryClick = (event) => {
    if (isModifiedClick(event)) return

    event.preventDefault()

    // ENG-11737: let the author check the destination without leaving LlamaVisor.
    if (preview) {
      window.open(primaryTarget.href, '_blank', 'noopener')
      return Promise.resolve()
    }

    // ENG-11743. window.open must run inside the click gesture, before the await below.
    if (primaryTarget.origin !== window.location.origin) {
      window.open(primaryTarget.href, '_blank', 'noopener')

      return report(ANNOUNCEMENT_EVENTS.PRIMARY_CTA).then(closeSilently)
    }

    // Navigate only once the click is recorded, or the unload races the request. report
    // resolves even on failure, so a dead endpoint cannot strand the reader here.
    return report(ANNOUNCEMENT_EVENTS.PRIMARY_CTA).then(() => {
      closeSilently()
      window.location.assign(primaryTarget.href)
    })
  }

  // Chained, not concurrent: both write the cookie-backed session dismissal list, so a
  // parallel billing_viewed would hand back the list it read before primary_cta landed.
  const handleUpgradeClick = () => {
    if (preview) return closeSilently()

    report(ANNOUNCEMENT_EVENTS.PRIMARY_CTA).then(() => report(ANNOUNCEMENT_EVENTS.BILLING_VIEWED))
    closeSilently()
    setPlansDialogOpen(true)
  }

  // Reports nothing on purpose: the only event that fits ("closed") writes a permanent
  // dismissal, retiring an offer the reader just engaged with.
  const handleContactClick = (event) => {
    if (isModifiedClick(event)) return

    event.preventDefault()
    closeSilently()
    setContactSalesOpen(true)
  }

  return (
    <>
      {/* Keys the Bootstrap reset in shared/_in_app_announcement_styling.html.erb, without
          which this portaled dialog renders flat on legacy pages. Its own class, not the
          bar's: .announcement-root also feeds the --cta-banner-offset script. Outside
          AppDialog so it outlives the close, for the PlansDialog opened after. */}
      <span className='announcement-modal-root' aria-hidden='true' hidden />

      <AppDialog open={open} onOpenChange={handleOpenChange}>
        <AppDialogContent
          size='md'
          onInteractOutside={handleInteractOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          <AppDialogHeader>
            <AppDialogTitle className='text-left'>{headline}</AppDialogTitle>
          </AppDialogHeader>

          <AppDialogBody className='flex flex-col gap-4 text-left'>
            {/* object-contain, not cover: authors bake copy into the creative, and a fixed
                frame would crop it away. */}
            {mediaUrl && (
              <img
                data-testid='announcement-modal-media'
                src={mediaUrl}
                alt=''
                loading='eager'
                className='w-full h-auto max-h-[320px] object-contain rounded-md bg-gray-100'
              />
            )}

            <div
              data-testid='announcement-modal-body'
              // Last paragraph inline so the contact link below reads as the end of that
              // sentence rather than dropping to a line of its own.
              className='text-sm leading-[150%] [&_p]:mb-2 [&_p:last-child]:mb-0 [&_p:last-child]:inline [&_a]:font-medium [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5'
            >
              {bodyHtml && (
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      bodyHtml,
                      ANNOUNCEMENT_BODY_ALLOWED_TAGS,
                      ANNOUNCEMENT_BODY_ALLOWED_ATTR,
                      ANNOUNCEMENT_BODY_SANITIZE_OPTIONS
                    ),
                  }}
                />
              )}
              {/* Rails lifts this out of the body for the bar's two-line clamp. The modal has
                  no clamp, so it goes back where it was authored. */}
              {contactCta?.label && contactCta?.url && (
                <>
                  {' '}
                  <a href={contactCta.url} onClick={handleContactClick} className='underline whitespace-nowrap'>
                    {contactCta.label}
                  </a>
                </>
              )}
            </div>
          </AppDialogBody>

          <AppDialogFooter>
            {secondaryCta?.label && (
              <AppButton variant='secondary' onClick={handleSecondaryClick}>
                {secondaryCta.label}
              </AppButton>
            )}

            {primaryCta?.label && primaryTarget && (
              isUpgradeNowCta ? (
                <AppButton onClick={handleUpgradeClick}>{primaryCta.label}</AppButton>
              ) : (
                // asChild so the destination is a real <a>: middle click and "open in new
                // tab" keep working, which window.location.assign alone loses.
                <AppButton asChild>
                  <a href={primaryCta.url} onClick={handlePrimaryClick}>{primaryCta.label}</a>
                </AppButton>
              )
            )}
          </AppDialogFooter>
        </AppDialogContent>
      </AppDialog>

      {isUpgradeNowCta && company && (
        <AppContext.Provider value={billingContextValue}>
          {/* No onSuccess: handleUpgradeClick already closed the announcement. */}
          <PlansDialog open={plansDialogOpen} onOpenChange={setPlansDialogOpen} />
        </AppContext.Provider>
      )}

      {contactSalesOpen && company && (
        <ContactSalesDialog
          open={contactSalesOpen}
          handleOpenChange={setContactSalesOpen}
        />
      )}
    </>
  )
}

export default InAppAnnouncementModal
